'use client';

import { useMemo } from 'react';
import type * as React from 'react';
import { cx } from '../utils/cx';
import { useKeyedAnimation } from '../utils/use-keyed-animation';
import { resolveSpaceUnits, type SpaceToken } from '../utils/spacing';

export type StackDirection = 'vertical' | 'horizontal';
export type StackAlign = 'start' | 'center' | 'end' | 'stretch' | 'baseline';
export type StackJustify = 'start' | 'center' | 'end' | 'between' | 'around' | 'evenly';
export type StackPreset = 'default' | 'scrollArea' | 'scrollList';

type VoidHtmlTags =
  | 'area'
  | 'base'
  | 'br'
  | 'col'
  | 'embed'
  | 'hr'
  | 'img'
  | 'input'
  | 'link'
  | 'meta'
  | 'param'
  | 'source'
  | 'track'
  | 'wbr';

export type StackIntrinsicTag = Exclude<keyof HTMLElementTagNameMap, VoidHtmlTags>;

export type StackSpace = number | SpaceToken | string;

export interface StackOwnProps {
  as?: StackIntrinsicTag;
  preset?: StackPreset;
  direction?: StackDirection;

  /**
   * Prefer semantic tokens for portability:
   * - "xs" | "sm" | "md" | "lg" | "xl" | "2xl"
   * Still supports numeric values (Tailwind scale-ish).
   */
  gap?: StackSpace;

  align?: StackAlign;
  justify?: StackJustify;
  wrap?: boolean;
  reverse?: boolean;
  inline?: boolean;
  divider?: boolean;
  fullWidth?: boolean;
  grow?: boolean;

  /** Padding supports spacing tokens/numbers. */
  padding?: StackSpace;

  animateKey?: string | number;
  animateClassName?: string;
  animateDurationMs?: number;
  animateOnMount?: boolean;
  className?: string;
  children?: React.ReactNode;
}

export type StackProps<T extends StackIntrinsicTag = 'div'> = StackOwnProps &
  Omit<React.ComponentPropsWithoutRef<T>, keyof StackOwnProps | 'as' | 'className' | 'children'> & {
    as?: T;
    className?: string;
    children?: React.ReactNode;
  };

const GAP_CLASS: Record<number, string> = {
  0: 'gap-0',
  1: 'gap-1',
  2: 'gap-2',
  3: 'gap-3',
  4: 'gap-4',
  5: 'gap-5',
  6: 'gap-6',
  7: 'gap-7',
  8: 'gap-8',
  9: 'gap-9',
  10: 'gap-10',
  11: 'gap-11',
  12: 'gap-12',
  14: 'gap-14',
  16: 'gap-16',
};

const PADDING_CLASS: Record<number, string> = {
  0: 'p-0',
  1: 'p-1',
  2: 'p-2',
  3: 'p-3',
  4: 'p-4',
  5: 'p-5',
  6: 'p-6',
  7: 'p-7',
  8: 'p-8',
  9: 'p-9',
  10: 'p-10',
  11: 'p-11',
  12: 'p-12',
};

function scaleClass(value: number | undefined, map: Record<number, string>, fallback: string): string {
  if (typeof value !== 'number' || Number.isNaN(value)) return fallback;
  if (map[value]) return map[value];
  const keys = Object.keys(map).map(Number).sort((a, b) => a - b);
  const clamped = Math.max(keys[0]!, Math.min(keys[keys.length - 1]!, Math.round(value)));
  return map[clamped] ?? fallback;
}

function gapClass(gap: StackSpace | undefined): string {
  const units = resolveSpaceUnits(gap, 4);
  return scaleClass(units, GAP_CLASS, GAP_CLASS[4]);
}

function paddingClass(padding: StackSpace | undefined): string | null {
  if (padding === undefined) return null;
  const units = resolveSpaceUnits(padding, 0);
  return scaleClass(units, PADDING_CLASS, PADDING_CLASS[0]);
}

function alignClass(align: StackAlign | undefined): string {
  switch (align) {
    case 'center':
      return 'items-center';
    case 'end':
      return 'items-end';
    case 'stretch':
      return 'items-stretch';
    case 'baseline':
      return 'items-baseline';
    case 'start':
    default:
      return 'items-start';
  }
}

function justifyClass(justify: StackJustify | undefined): string {
  switch (justify) {
    case 'center':
      return 'justify-center';
    case 'end':
      return 'justify-end';
    case 'between':
      return 'justify-between';
    case 'around':
      return 'justify-around';
    case 'evenly':
      return 'justify-evenly';
    case 'start':
    default:
      return 'justify-start';
  }
}

function directionClass(direction: StackDirection, reverse: boolean): string {
  if (direction === 'horizontal') return reverse ? 'flex-row-reverse' : 'flex-row';
  return reverse ? 'flex-col-reverse' : 'flex-col';
}

function dividerClass(direction: StackDirection, reverse: boolean): string | null {
  if (direction === 'horizontal') return reverse ? 'divide-x-reverse divide-x' : 'divide-x';
  return reverse ? 'divide-y-reverse divide-y' : 'divide-y';
}

function presetClass(preset: StackPreset): string {
  switch (preset) {
    case 'scrollArea':
      // The extra `basis-0`, `min-h-0`, and `max-h-full` constraints make the
      // stack behave predictably as a scrollable flex child in column layouts.
      return 'flex-1 basis-0 min-h-0 min-w-0 max-h-full overflow-y-auto overflow-x-hidden overscroll-contain touch-pan-y';
    case 'scrollList':
      // A cleaner scroll preset for message feeds and other data-heavy lists.
      // It keeps the scroll behavior, but avoids extra sizing assumptions.
      return 'flex-1 basis-0 min-h-0 min-w-0 overflow-y-auto overflow-x-hidden overscroll-contain touch-pan-y';
    case 'default':
    default:
      return '';
  }
}

export function Stack<T extends StackIntrinsicTag = 'div'>(props: StackProps<T>) {
  const {
    as,
    preset = 'default',
    direction = 'vertical',
    gap = 'md',
    align,
    justify,
    wrap = false,
    reverse = false,
    inline = false,
    divider = false,
    fullWidth = false,
    grow = false,
    padding,
    className,
    children,
    animateKey,
    animateClassName,
    animateDurationMs = 350,
    animateOnMount = false,
    ...rest
  } = props;

  const Component = (as ?? 'div') as React.ElementType;

  const shouldAnimate = useKeyedAnimation({
    keyValue: animateKey,
    enabled: Boolean(animateClassName && animateClassName.trim()),
    durationMs: animateDurationMs,
    animateOnMount,
  });

  const keyedAnimCls = useMemo(() => {
    if (!shouldAnimate) return null;
    const trimmed = (animateClassName ?? '').trim();
    return trimmed ? trimmed : null;
  }, [animateClassName, shouldAnimate]);

  const resolvedAlign = align ?? (preset === 'scrollList' ? 'stretch' : 'start');
  const resolvedJustify = justify ?? 'start';

  return (
    <Component
      {...rest}
      className={cx(
        inline ? 'inline-flex' : 'flex',
        'min-w-0 min-h-0',
        presetClass(preset),
        directionClass(direction, reverse),
        gapClass(gap),
        alignClass(resolvedAlign),
        justifyClass(resolvedJustify),
        wrap ? 'flex-wrap' : 'flex-nowrap',
        fullWidth ? 'w-full' : null,
        grow ? 'flex-1' : null,
        paddingClass(padding),
        divider ? dividerClass(direction, reverse) : null,
        keyedAnimCls,
        className,
      )}
    >
      {children}
    </Component>
  );
}

Stack.displayName = 'Stack';
